import {
  formatDateString,
  formatRelativeDateString,
} from "@/v2-common/utils/utils.date"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import cn from "@/v2-ui/utils/utils.cn"
import Icon from "@/v2-ui/icon"
import type { IconNames } from "@/v2-ui/icon/icon.types"
import Badge from "@/v2-backoffice-client/components/badge"
import Link from "@/v2-backoffice-client/components/link"
import { TooltipButton } from "@/v2-backoffice-client/components/button"
import { buildCustomerViewLink } from "@/v2-backoffice-client/customer/customer.links"
import { formatCompactAmount } from "@/v2-backoffice-client/customer/billing/customerBilling.utils"
import { useStaffNotificationsMarkReadMutation } from "@/v2-backoffice-client/notification/markRead/notification.markRead.mutation"
import { CUSTOMER_CRM_MERGES_HREF } from "@/v2-backoffice-client/customer/customer.constants"
import {
  NOTIFICATION_CODE_CUSTOMER_ACTIVITY_DROPPED,
  NOTIFICATION_CODE_CUSTOMER_LICENSE_COVERAGE_GAP,
  NOTIFICATION_CODE_CUSTOMER_LICENSE_DEMAND,
  NOTIFICATION_CODE_CUSTOMER_MERGE_CONSOLIDATED,
  NOTIFICATION_CODE_CUSTOMER_MERGE_NEEDS_DECISION,
  NOTIFICATION_CODE_CUSTOMER_OVER_SEAT_CAP,
  NOTIFICATION_CODE_CUSTOMER_PAYMENT_OVERDUE,
  NOTIFICATION_CODE_CUSTOMER_TRIAL_ENDING_SOON,
  NOTIFICATION_CODE_CUSTOMER_WENT_SILENT,
  NOTIFICATION_CODE_SELF_SERVICE_INVITE_CREATED,
} from "@/v2-backoffice-client/notification/notification.constants"
import {
  parseNotificationPayload,
  type StaffNotificationMergeSide,
  type StaffNotificationPayload,
} from "@/v2-backoffice-client/notification/notification.utils"
import type { StaffNotificationsQuery } from "@/v2-backoffice-client/notification/feed/__types__/notification.feed.query"

export type StaffNotificationRow =
  StaffNotificationsQuery["getStaffNotifications"][number]

export type NotificationFeedItemProps = {
  notification: StaffNotificationRow
  className?: string
}

// Tinted like badge.css's pill language — the hue alone tells the
// operator what kind of event they're scanning: red act-now, amber
// attention, green good, grey no-signal.
type NotificationTone = "critical" | "warning" | "positive" | "neutral"

const TONE_CLASSES: Record<NotificationTone, string> = {
  critical: "badge-critical",
  warning: "badge-warning",
  positive: "badge-positive",
  neutral: "badge-neutral",
}

// Occupied seats as the producer saw them: users plus open invitations.
function seatsUsedOf(payload: StaffNotificationPayload | null) {
  return (payload?.userCount ?? 0) + (payload?.pendingInvitationCount ?? 0)
}

// Human sentence + glyph + tone per event code; unknown codes (from newer
// backends) fall back to the raw code so the feed never renders blank.
// The alert sentences reuse deriveChurnRisk's reason wording where one
// exists, so the feed and the churn badge speak the same language.
function describeNotification(
  notification: StaffNotificationRow,
  payload: StaffNotificationPayload | null,
): {
  title: string
  description: string | null
  iconSrc: IconNames
  tone: NotificationTone
} {
  if (notification.Code === NOTIFICATION_CODE_SELF_SERVICE_INVITE_CREATED) {
    const inviter = payload?.inviterName || "A member"
    const invitee = payload?.inviteeEmail || "a coworker"
    // An invite that takes the customer past its seat cap is a different
    // event to the reader: not "good, they are growing" but "sales has a
    // seat count to agree". Seats are never enforced, so this row is how
    // they hear of it at the moment it happens.
    if (payload?.licensesExceeded) {
      return {
        title: "Invitation beyond the seat cap",
        description: `${inviter} invited ${invitee}; the customer now has ${formatNumber(seatsUsedOf(payload))} seats in use on ${formatNumber(payload.userLicenses ?? 0)} licensed. Nothing is blocked — the seat count needs agreeing with the customer.`,
        iconSrc: "triangle-warning",
        tone: "warning",
      }
    }
    return {
      title: "Self-service invitation",
      description: `${inviter} invited ${invitee} to the team`,
      iconSrc: "user-add",
      tone: "positive",
    }
  }
  if (notification.Code === NOTIFICATION_CODE_CUSTOMER_OVER_SEAT_CAP) {
    const licensed = payload?.userLicenses ?? 0
    const used = seatsUsedOf(payload)
    return {
      title: "Over the seat cap",
      description:
        payload?.userLicenses != null
          ? `${formatNumber(used)} seats in use (${formatNumber(payload.userCount ?? 0)} users plus ${formatNumber(payload.pendingInvitationCount ?? 0)} open invitations) on ${formatNumber(licensed)} licensed — ${formatNumber(used - licensed)} over. Nothing is blocked; agree the seat count with the customer and set it on the Licensing tab.`
          : "More seats in use than the live licenses allow",
      iconSrc: "triangle-warning",
      tone: "warning",
    }
  }
  if (notification.Code === NOTIFICATION_CODE_CUSTOMER_WENT_SILENT) {
    return {
      title: "Customer went silent",
      description:
        payload?.seatCount != null
          ? `No seat has been active in the last 28 days — all ${formatNumber(payload.seatCount)} went quiet after ${formatNumber(payload.weightedPrev28d ?? 0)} weighted actions the window before`
          : "No seat has been active in the last 28 days",
      iconSrc: "bell-slash",
      tone: "critical",
    }
  }
  if (notification.Code === NOTIFICATION_CODE_CUSTOMER_ACTIVITY_DROPPED) {
    return {
      title: "Usage dropped sharply",
      description:
        payload?.trendPct != null
          ? `Usage is down ${Math.abs(payload.trendPct).toFixed(0)}% vs the previous 28 days (${formatNumber(payload.weighted28d ?? 0)} vs ${formatNumber(payload.weightedPrev28d ?? 0)} weighted actions)`
          : "Usage is down 50%+ vs the previous 28 days",
      iconSrc: "arrow-trend-down",
      tone: "warning",
    }
  }
  if (notification.Code === NOTIFICATION_CODE_CUSTOMER_TRIAL_ENDING_SOON) {
    const ends = payload?.trialEndsAt
      ? formatDateString(payload.trialEndsAt)
      : "within a week"
    const activation =
      payload?.seatCount != null
        ? ` — ${formatNumber(payload.activeCount ?? 0)} of ${formatNumber(payload.seatCount)} seats active in the last 28 days`
        : ""
    return {
      title: "Trial ending soon",
      description: `Trial ends ${ends}${activation}`,
      iconSrc: "hourglass-end",
      tone: "warning",
    }
  }
  if (notification.Code === NOTIFICATION_CODE_CUSTOMER_LICENSE_COVERAGE_GAP) {
    const people = payload?.users7d ?? 0
    const attempts = payload?.attempts7d ?? 0
    const more =
      payload?.issueCount && payload.issueCount > 1
        ? ` ${payload.issueCount - 1} more license${payload.issueCount - 1 === 1 ? "" : "s"} of theirs ${payload.issueCount - 1 === 1 ? "has" : "have"} a hole too.`
        : ""
    return {
      title: payload?.productName
        ? `Hole in ${payload.productName} access`
        : "Hole in license access",
      description: `${payload?.message ?? "Billing, invoicing and access disagree on one of the customer's licenses, and nobody is let in."}${
        attempts > 0
          ? ` ${formatNumber(people)} ${people === 1 ? "person" : "people"} tried ${formatNumber(attempts)} time${attempts === 1 ? "" : "s"} this week.`
          : ""
      }${payload?.hint ? ` ${payload.hint}` : ""}${more}`,
      iconSrc: "calendar-xmark",
      tone: "critical",
    }
  }
  if (notification.Code === NOTIFICATION_CODE_CUSTOMER_LICENSE_DEMAND) {
    const attempts = payload?.attempts7d ?? 0
    const people = payload?.users7d ?? 0
    const features = payload?.features7d ?? 0
    const topFeature = payload?.topFeatureName || payload?.topFeatureCode
    return {
      title: "Knocking on a license lock",
      description:
        attempts > 0
          ? `${formatNumber(people)} ${people === 1 ? "person" : "people"} tried ${formatNumber(attempts)} time${attempts === 1 ? "" : "s"} in the last 7 days to use ${formatNumber(features)} feature${features === 1 ? "" : "s"} the license does not carry${
              topFeature
                ? ` — most of all ${topFeature}${payload?.topFeatureAttempts ? ` (${formatNumber(payload.topFeatureAttempts)})` : ""}`
                : ""
            }. Nothing is broken; the customer is asking for more than it bought.`
          : "People at the customer keep meeting the license lock — the customer is asking for more than it bought.",
      iconSrc: "door-closed",
      tone: "warning",
    }
  }
  if (notification.Code === NOTIFICATION_CODE_CUSTOMER_PAYMENT_OVERDUE) {
    return {
      title: "Payment overdue",
      description:
        payload?.overdueBalance != null
          ? `${formatCompactAmount(payload.overdueBalance, payload.currency)} is past due${
              payload.outstandingBalance != null &&
              payload.outstandingBalance > payload.overdueBalance
                ? ` (${formatCompactAmount(payload.outstandingBalance, payload.currency)} outstanding in total)`
                : ""
            }`
          : "The invoice mirror shows money past due",
      iconSrc: "file-invoice-dollar",
      tone: "critical",
    }
  }
  if (notification.Code === NOTIFICATION_CODE_CUSTOMER_MERGE_CONSOLIDATED) {
    const keeper = mergeSideName(payload, payload?.keeperCustomerId)
    const emptied = mergeSideName(payload, payload?.emptiedCustomerId)
    const moved = describeMergeMoved(
      payload?.keeperCustomerId === payload?.leftover?.customerId
        ? payload?.survivor
        : payload?.leftover,
    )
    return {
      title: "HubSpot merge consolidated",
      description: `HubSpot merged ${mergeCompanyName(payload?.leftover, payload?.mergedExternalCompanyId)} into ${mergeCompanyName(payload?.survivor, payload?.survivingExternalCompanyId)}; ${moved} moved from ${emptied} onto ${keeper}${payload?.linkMoves ? ", and the company link followed" : ""}. Undo on CRM Merges if the two should stay separate customers.`,
      iconSrc: "code-merge",
      tone: "positive",
    }
  }
  if (notification.Code === NOTIFICATION_CODE_CUSTOMER_MERGE_NEEDS_DECISION) {
    return {
      title: "HubSpot merge needs a decision",
      description: `HubSpot merged ${mergeCompanyName(payload?.leftover, payload?.mergedExternalCompanyId)} into ${mergeCompanyName(payload?.survivor, payload?.survivingExternalCompanyId)}, and both customer rows hold something a machine must not move (${describeMergeBlockerShort(payload?.blocker)}). Pick the row that keeps, clear the blocker, or keep them separate.`,
      iconSrc: "code-merge",
      tone: "warning",
    }
  }
  return {
    title: notification.Code,
    description: null,
    iconSrc: "bell",
    tone: "neutral",
  }
}

// The merge payload's two rows by name, with the HubSpot company id as the
// fallback for a row we never had.
function mergeCompanyName(
  side: StaffNotificationMergeSide | undefined,
  externalCompanyId: string | undefined,
) {
  return side?.name ?? `company ${externalCompanyId ?? "?"}`
}

function mergeSideName(
  payload: StaffNotificationPayload | null,
  customerId: number | undefined,
) {
  if (customerId == null) return "the keeper"
  const side = [payload?.leftover, payload?.survivor].find(
    (candidate) => candidate?.customerId === customerId,
  )
  return side?.name ?? `customer #${customerId}`
}

function describeMergeMoved(side: StaffNotificationMergeSide | undefined) {
  const parts = [
    side?.tenants
      ? `${side.tenants} team${side.tenants === 1 ? "" : "s"}`
      : null,
    side?.users ? `${side.users} user${side.users === 1 ? "" : "s"}` : null,
  ].filter((part): part is string => part !== null)
  return parts.length > 0 ? parts.join(" and ") : "its people"
}

function describeMergeBlockerShort(blocker: string | undefined) {
  switch (blocker) {
    case "BothHoldMoney":
      return "both hold a licence or a billing account"
    case "EmptiedHoldsConfig":
      return "the row to empty holds an add-on, access list, closed class or collections owner"
    case "KeeperHasNoKey":
      return "the keeper has no legacy HubSpot id"
    default:
      return "see the row"
  }
}

// The one-click "go handle it" per event code: each alert lands on the
// workbench that explains the number and holds the levers — the same tab
// targets the customers board and portfolio deep links use.
function getNotificationAction(
  notification: StaffNotificationRow,
  payload: StaffNotificationPayload | null,
): { label: string; to: string } | null {
  const externalCustomerId = notification.ExternalCustomerId

  if (notification.Code === NOTIFICATION_CODE_SELF_SERVICE_INVITE_CREATED) {
    // Past the cap, the thing to handle is the seat count, not the invite.
    if (payload?.licensesExceeded && externalCustomerId) {
      return {
        label: "Fix seats",
        to: buildCustomerViewLink(externalCustomerId, "billing"),
      }
    }
    return { label: "Open invitations", to: "/invitations" }
  }
  if (
    notification.Code === NOTIFICATION_CODE_CUSTOMER_MERGE_CONSOLIDATED ||
    notification.Code === NOTIFICATION_CODE_CUSTOMER_MERGE_NEEDS_DECISION
  ) {
    // The merge row holds both customers, what each holds, and the
    // choices (keep one, undo, keep separate).
    return { label: "Review the merge", to: CUSTOMER_CRM_MERGES_HREF }
  }
  if (!externalCustomerId) return null

  if (notification.Code === NOTIFICATION_CODE_CUSTOMER_OVER_SEAT_CAP) {
    return {
      label: "Fix seats",
      to: buildCustomerViewLink(externalCustomerId, "billing"),
    }
  }

  if (
    notification.Code === NOTIFICATION_CODE_CUSTOMER_WENT_SILENT ||
    notification.Code === NOTIFICATION_CODE_CUSTOMER_ACTIVITY_DROPPED
  ) {
    return {
      label: "Review activity",
      to: buildCustomerViewLink(externalCustomerId, "sign-ins"),
    }
  }
  if (notification.Code === NOTIFICATION_CODE_CUSTOMER_TRIAL_ENDING_SOON) {
    return {
      label: "Review licensing",
      to: buildCustomerViewLink(externalCustomerId, "billing"),
    }
  }
  if (notification.Code === NOTIFICATION_CODE_CUSTOMER_LICENSE_COVERAGE_GAP) {
    // The licenses tab carries the coverage notice with the bridge and the
    // Fenerum link beside the hole.
    return {
      label: "Review licenses",
      to: buildCustomerViewLink(externalCustomerId, "billing"),
    }
  }
  if (notification.Code === NOTIFICATION_CODE_CUSTOMER_LICENSE_DEMAND) {
    // The thing to handle is what they get: the callout there names the
    // features and the editor the packages that would unlock them.
    return {
      label: "Review what they get",
      to: buildCustomerViewLink(externalCustomerId, "feature-access"),
    }
  }
  if (notification.Code === NOTIFICATION_CODE_CUSTOMER_PAYMENT_OVERDUE) {
    return {
      label: "Review billing",
      to: buildCustomerViewLink(externalCustomerId, "billing"),
    }
  }
  return null
}

function NotificationFeedItem(props: NotificationFeedItemProps) {
  const { notification, className } = props

  const [markRead, { loading: isMarking }] =
    useStaffNotificationsMarkReadMutation()

  const payload = parseNotificationPayload(notification.Payload)
  const { title, description, iconSrc, tone } = describeNotification(
    notification,
    payload,
  )
  const action = getNotificationAction(notification, payload)
  const isUnread = !notification.ReadAt

  // Seat math as it stood when the event happened (written into the
  // payload by the producer, so history stays truthful even after the
  // customer buys more licenses).
  const hasSeatFacts = payload?.userLicenses != null
  const seatsUsed = seatsUsedOf(payload)

  // The money behind a churn alert — same "priced churn" the boards put on
  // their at-risk badges (12-month billed revenue at evaluation time).
  const isChurnAlert =
    notification.Code === NOTIFICATION_CODE_CUSTOMER_WENT_SILENT ||
    notification.Code === NOTIFICATION_CODE_CUSTOMER_ACTIVITY_DROPPED
  const atStake =
    isChurnAlert && payload?.billed12M != null && payload.billed12M > 0
      ? formatCompactAmount(payload.billed12M, payload.currency)
      : null

  // The tenant link only earns its place when it adds information —
  // single-team customers usually share one name across both.
  const hasDistinctTenant =
    !!notification.TenantName &&
    notification.TenantName !== notification.CustomerName

  return (
    <li
      className={cn(
        "notification-feed-item group flex gap-3 px-4 py-2.5 transition-colors hover:bg-muted/40",
        isUnread && "bg-primary/5",
        className,
      )}
    >
      {/* Type-tinted circle (badge.css tint classes), dimmed once read
          so the unread rows carry the color weight. */}
      <span
        aria-hidden
        className={cn(
          "mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full",
          TONE_CLASSES[tone],
          !isUnread && "opacity-50",
        )}
      >
        <Icon src={iconSrc} size={14} />
      </span>

      <div className="min-w-0 flex-1 space-y-0.5">
        <div className="flex items-start justify-between gap-3">
          <p className="text-sm font-medium leading-snug">
            {title}
            {isUnread ? (
              <span
                aria-label="Unread"
                className="ml-1.5 inline-block size-1.5 -translate-y-px rounded-full bg-primary"
              />
            ) : null}
          </p>

          <span className="flex h-6 shrink-0 items-center gap-1">
            <time
              dateTime={notification.CreatedAt}
              title={notification.CreatedAt}
              className="whitespace-nowrap text-[11px] text-muted-foreground"
            >
              {formatRelativeDateString(notification.CreatedAt)}
            </time>
            {/* Read-state toggle on every row: check marks it read,
                undo hands it back to the queue for someone else. */}
            {isUnread ? (
              <TooltipButton
                variant="ghost"
                size="tiny"
                iconSrc="check"
                content="Mark as read"
                isLoading={isMarking}
                className="text-muted-foreground hover:text-foreground"
                data-feature-code="UI_BackofficeNotifications_Feed_MarkRead"
                onClick={() => {
                  markRead({ variables: { ids: [notification.Id] } })
                }}
              />
            ) : (
              <TooltipButton
                variant="ghost"
                size="tiny"
                iconSrc="undo"
                content="Mark as unread"
                isLoading={isMarking}
                className="text-muted-foreground opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100 hover:text-foreground"
                data-feature-code="UI_BackofficeNotifications_Feed_MarkUnread"
                onClick={() => {
                  markRead({
                    variables: { ids: [notification.Id], unread: true },
                  })
                }}
              />
            )}
          </span>
        </div>

        {description ? (
          <p className="text-sm leading-snug text-muted-foreground">
            {description}
          </p>
        ) : null}

        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 pt-1 text-xs text-muted-foreground">
          {notification.CustomerName && notification.ExternalCustomerId ? (
            <Link
              to={buildCustomerViewLink(notification.ExternalCustomerId)}
              className="font-medium text-foreground/80 hover:text-foreground hover:underline"
            >
              {notification.CustomerName}
            </Link>
          ) : null}

          {hasDistinctTenant && notification.TenantId ? (
            <>
              <span aria-hidden>·</span>
              <Link
                to={`?v=tenant:${notification.TenantId}`}
                className="hover:text-foreground hover:underline"
              >
                {notification.TenantName}
              </Link>
            </>
          ) : null}

          {hasSeatFacts ? (
            <Badge
              size="xs"
              variant={payload?.licensesExceeded ? "warning" : "neutral"}
            >
              {seatsUsed} of {payload?.userLicenses} seats
            </Badge>
          ) : null}

          {atStake ? (
            <Badge
              size="xs"
              variant="warning"
              title="Billed the last 12 months — what this account is worth if the churn signal plays out."
            >
              {atStake} at stake
            </Badge>
          ) : null}

          {!isUnread && notification.ReadByName ? (
            <span className="ml-auto whitespace-nowrap">
              Read by {notification.ReadByName}
            </span>
          ) : null}
        </div>

        {action ? (
          <div className="pt-1">
            <Link
              to={action.to}
              variant="unstyled"
              iconSrc={null}
              className="inline-flex items-center gap-1 rounded border border-border px-2 py-0.5 text-xs font-medium text-foreground/80 transition-colors hover:bg-muted hover:text-foreground"
              data-feature-code="UI_BackofficeNotifications_Feed_QuickAction"
              onClick={() => {
                // Following the action IS handling the notification — fold
                // the bookkeeping into the click so triage is one gesture.
                if (isUnread) {
                  markRead({ variables: { ids: [notification.Id] } })
                }
              }}
            >
              {action.label}
              <Icon src="arrow-right" size={10} aria-hidden />
            </Link>
          </div>
        ) : null}
      </div>
    </li>
  )
}

export default NotificationFeedItem
